import type { MaybeRef } from '@vueuse/core';
import { get } from '@vueuse/core';
import { computed, nextTick, ref } from 'vue';
import { deepCompareComputed } from '@/composable/deepCompareComputed';
import { useState } from '@/composable/useState';
import { assignArray } from '@/core/lib/array';
import { isInvalidLiveMp1X2Odds, isValidOdds } from '@/core/lib/oddsHelper';
import { deepFreeze } from '@/core/lib/utils';
import { useOddsToken } from '@/core/oddsApi/composable/useOddsToken';
import { formatOdds } from '@/core/oddsApi/helpers/formatOdds';
import type { EnumPresetFilterType } from '@/core/oddsApi/oddsApiType';
import { EnumOddsCategoryType, useOddsQuery } from '@/core/oddsApi/oddsApiType';
import type { IOdds } from '@/interface/IOdds';
import type { LeagueSize, SportType } from '@/interface/enum';
import { PriceStyle, TokenVersion } from '@/interface/enum';

export function useTicketOdds({
    eventId,
    oddsId,
    presetFilter,
    oddsCategory,
    priceStyle,
    isActive = true,
    sportType,
    leagueSize,
}: {
    eventId: number;
    oddsId?: number;
    presetFilter: EnumPresetFilterType;
    oddsCategory: EnumOddsCategoryType;
    priceStyle: MaybeRef<PriceStyle>;
    isActive?: MaybeRef<boolean>;
    sportType: SportType;
    leagueSize: LeagueSize;
}) {
    const { getOddsToken } = useOddsToken();

    const variables = computed(() => {
        const oddsTokenResult = getOddsToken(sportType, leagueSize);

        return {
            query: {
                id: eventId,
                oddsId,
                filter: presetFilter,
                oddsCategory,
                priceStyle: PriceStyle.toOddsApi(get(priceStyle)),
                oddsToken: oddsTokenResult.token,
                version: TokenVersion.toOddsApi(oddsTokenResult.version),
            },
        };
    });

    const [isEnabled, setIsEnabled] = useState(true);
    const refetch = async () => {
        if (isEnabled.value) {
            setIsEnabled(false);
            nextTick(() => setIsEnabled(true));
        }
    };
    const { onResult, loaded, loading } = useOddsQuery(
        variables,
        deepCompareComputed(() => ({
            enabled: get(isActive) && isEnabled.value && !!variables.value.query.oddsToken,
        })),
    );

    const oddsList = ref<IOdds[]>([]);

    onResult((result) => {
        const isMixParlayMode = oddsCategory === EnumOddsCategoryType.MixParlay;
        const newOddsList = (result ?? [])
            .filter(odds => odds.eventResult?.marketGroup)
            .filter(odds => oddsId && odds.id === oddsId)
            .map(odds => deepFreeze(formatOdds(odds)))
            .filter(odds => isValidOdds(odds) && !isInvalidLiveMp1X2Odds(odds, isMixParlayMode));
        assignArray(oddsList.value, newOddsList, item => item.id);
    });

    return {
        loaded,
        loading,
        oddsList,
        refetch,
    };
}
